import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './MetaAudiencesPage.css';

function formatInteger(value) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function formatDateTime(value) {
  if (!value) return 'Never';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return 'Never';
  return `${new Date(ms).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`;
}

export default function MetaAudiencesPage() {
  const { currentUser } = useAuth();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingKey, setDownloadingKey] = useState('');
  const [downloadingAll, setDownloadingAll] = useState(false);

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadSegments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await authedFetch('/api/v1/meta-audience-segments');
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.message || 'Failed to load audience segments.');
      setSegments(data.segments || []);
    } catch (err) {
      setError(err.message || 'Failed to load audience segments.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  const downloadCsv = useCallback(async (key) => {
    setDownloadingKey(key);
    setError('');
    try {
      const resp = await authedFetch(`/api/v1/meta-audience-segments/${encodeURIComponent(key)}/csv`);
      if (!resp.ok) {
        const data = await resp.json().catch(() => null);
        throw new Error(data?.message || 'Failed to generate CSV.');
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${key}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to generate CSV.');
    } finally {
      setDownloadingKey('');
    }
  }, [authedFetch]);

  const downloadAll = useCallback(async () => {
    setDownloadingAll(true);
    setError('');
    try {
      for (const segment of segments) {
        // eslint-disable-next-line no-await-in-loop -- sequential so downloads don't get blocked as a popup burst
        await downloadCsv(segment.key);
      }
    } finally {
      setDownloadingAll(false);
    }
  }, [segments, downloadCsv]);

  return (
    <div className="meta-audiences-page">
      <div className="meta-audiences-header">
        <div>
          <h1>Meta Audiences</h1>
          <p>
            The 7 Custom Audience segments used on ad account 509246833726940, sourced from
            customers_v9_source. Download a CSV and upload it in Ads Manager with
            &quot;Replace customers&quot; to refresh an audience.
          </p>
        </div>
        <button type="button" className="meta-audiences-refresh" onClick={loadSegments} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh counts'}
        </button>
      </div>

      {error && <div className="meta-audiences-alert meta-audiences-alert-error">{error}</div>}
      {!error && (
        <div className="meta-audiences-alert meta-audiences-alert-info">
          Each CSV includes email, phone, fn, ln, zip, ct, st, country, value (lifetime spend) —
          matching the column set Meta expects for a value-based customer list.
        </div>
      )}

      {segments.length > 0 && (
        <div className="meta-audiences-download-all">
          <button type="button" className="meta-audiences-download" onClick={downloadAll} disabled={downloadingAll || loading}>
            {downloadingAll ? 'Downloading all…' : 'Download all 7 CSVs'}
          </button>
        </div>
      )}

      <div className="meta-audiences-table-wrap">
        <table className="meta-audiences-table">
          <thead>
            <tr>
              <th>Audience</th>
              <th>Current customers</th>
              <th>Last CSV/sync snapshot</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            {loading && segments.length === 0 && (
              <tr><td colSpan={4}>Loading…</td></tr>
            )}
            {!loading && segments.length === 0 && !error && (
              <tr><td colSpan={4}>No segments found.</td></tr>
            )}
            {segments.map((segment) => (
              <tr key={segment.key}>
                <td>{segment.name}</td>
                <td className="meta-audiences-count">{formatInteger(segment.count)}</td>
                <td>{formatDateTime(segment.lastSyncedAt)}</td>
                <td>
                  <button
                    type="button"
                    className="meta-audiences-download"
                    onClick={() => downloadCsv(segment.key)}
                    disabled={downloadingKey === segment.key || downloadingAll}
                  >
                    {downloadingKey === segment.key ? 'Generating…' : 'Download CSV'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
